// ════════════════════════════════════════════════════════════════════
// TERROR — H9 · THE TITLES PIPELINE
// ────────────────────────────────────────────────────────────────────
// "Every achievement family terminates in an equippable title; title
//  rarity mirrors FABLE / LEGENDARY / MYTHIC."  — the roadmap, H9
//
// ── WHAT THIS IS, IN ONE BREATH ──────────────────────────────────────
// The achievement registry is a flat list of 75 things you can do. A
// FAMILY is the arc those things form — "collect cards", "go deep",
// "walk the story" — and the title is the arc's last page. Finish the
// family, wear the sentence.
//
// ── THREE THINGS THIS FILE DELIBERATELY DOES NOT DO ──────────────────
//
// 1. IT DOES NOT MINT A SEVENTH COSMETIC SLOT. Terror has had a `title`
//    slot since the cosmetic schema was locked, with fourteen strings
//    already in CosmeticManifest. A title earned here is a NORMAL
//    cosmetic unlock in that same slot, granted by the same
//    grantCosmetic() every other cosmetic uses. Nothing new to equip,
//    nothing new to render, no second wardrobe.
//
// 2. IT DOES NOT INVENT A FOURTH LADDER. THE THREE-LEVEL LAW says
//    everything ranked tops out at three, and achievements ALREADY
//    carry a three-rung ladder — beb / champ / lord, in
//    achievements.js. A title's tier is `rankOf(capstone)` run through
//    one map. Fable is a Beb's title, Legendary is a Champ's, Mythic is
//    a Lord's. If the rank ladder moves, the titles move with it, for
//    free, because there is exactly one ladder.
//
// 3. IT DOES NOT GUESS FAMILIES FROM ID STRINGS. `collector_50` and
//    `void_collector` are the same arc and no regex on earth reads
//    that. Families are AUTHORED below, one table, and an achievement
//    that belongs to no family is not a bug — it is a one-shot. The
//    audit at the bottom prints the orphans instead of pretending.
//
// ── THE EXCEPTION LAW, BUILT IN BEFORE IT WAS ASKED ──────────────────
// A family may author `tier` to override the derived one, and may
// author `titleText` to override the generated display string. Neither
// is used today. Both exist because the first family that wants to be
// louder than its capstone should be a one-line edit, not a session.
//
// ⚠ PLACEHOLDER PROSE. Every `titleText` and `blurb` below is V'ael's
// draft, written to prove the seam carries words. caP'n's canon veto
// stands over every one of them — they are rows in a table, and
// replacing one costs a string.
// ════════════════════════════════════════════════════════════════════

import {
  ACHIEVEMENTS,
  GAME_NAMESPACES,
  rankOf,
} from "./achievements";
import { grantCosmetic, getUnlocked } from "./progression";
import { COSMETICS } from "./CosmeticManifest";

// ── THE THREE TIERS ───────────────────────────────────────────────────
// Same three words the Fountain and the Story Set use, same order,
// rarest last. One vocabulary across the whole title.
export const TITLE_TIERS = ["fable", "legendary", "mythic"];

export const TITLE_TIER_META = {
  fable:     { order: 0, label: "FABLE",     color: "#8fb8e8",
               blurb: "You finished the arc. Most people don't." },
  legendary: { order: 1, label: "LEGENDARY", color: "#e8b84b",
               blurb: "You finished a hard arc, or finished an easy one all the way." },
  mythic:    { order: 2, label: "MYTHIC",    color: "#f43f5e",
               blurb: "The top of the ladder. There is no fourth." },
};

if (TITLE_TIERS.length !== 3) {
  // eslint-disable-next-line no-console
  console.error("[TERROR/TITLES] THE THREE-LEVEL LAW: three tiers. Found " + TITLE_TIERS.length + ".");
}

// The one bridge off the achievement rank ladder. Read it as: a Beb's
// arc is a Fable, a Champ's arc is a Legend, a Lord's arc is a Myth.
const RANK_TO_TIER = { beb: "fable", champ: "legendary", lord: "mythic" };

// And the one bridge INTO the cosmetic schema. `unlockedCosmetics`
// validates rarity against the locked six-tier cosmetic ladder, which
// has no "fable" rung — so a Fable title is stored as `rare`. This is a
// STORAGE mapping only; nothing reads it back to decide a title's tier,
// which is always derived from the registry. If the cosmetic ladder
// ever grows a fable rung, delete this map and nothing else moves.
const TIER_TO_COSMETIC_RARITY = { fable: "rare", legendary: "legendary", mythic: "mythic" };

// ── THE FAMILIES ──────────────────────────────────────────────────────
// Each row:
//   id           — permanent. Becomes the cosmetic id as `title_<id>`.
//   game         — origin namespace (drives the tint, matches ACHIEVEMENTS).
//   name         — the family's own name, for the gallery header.
//   achievements — every member, IN ORDER. The LAST one is the capstone,
//                  and the capstone alone decides the tier.
//   titleText    — what the player actually wears. Nameplates read
//                  "caP'n, <titleText>", so lowercase articles are
//                  deliberate: "the Unbroken", not "The Unbroken".
//   blurb        — one line of why, for the gallery.
//   tier         — OPTIONAL override. Unused today. See THE EXCEPTION LAW.
//
// A family needs at least one member and every member must exist in
// ACHIEVEMENTS — auditFamilies() below proves both.
export const TITLE_FAMILIES = [
  // ── TERROR ──────────────────────────────────────────────────────────
  {
    id: "first_light", game: "terror", name: "First Light",
    achievements: ["terror:first_pack", "terror:first_blood", "terror:first_freeplay"],
    titleText: "the Newly Woken",
    blurb: "You opened a pack, you won a Solrend, and you did it once more on your own terms.",
  },
  {
    id: "collector", game: "terror", name: "The Collection",
    achievements: ["terror:collector_50", "terror:collector_100", "terror:void_collector", "terror:collector_all"],
    titleText: "Keeper of the Whole Codex",
    blurb: "Every card that exists, held once. The collection is the soul.",
  },
  {
    id: "foilwright", game: "terror", name: "The Shine",
    achievements: ["terror:first_holo", "terror:holo_5", "terror:jynxi_holo"],
    titleText: "the Foilwright",
    blurb: "You pulled the light out of the paper five times, and once out of her.",
  },
  {
    id: "descent", game: "terror", name: "The Descent",
    achievements: [
      "terror:depths_solrend", "terror:gatekeeper_first", "terror:depths_200_wins",
      "terror:streak_50", "terror:depths_500_wins", "terror:depths_champion",
      "terror:depths_aenarchist",
    ],
    titleText: "Aenarchist of the Endless Depths",
    blurb: "Down past where the shaft stops being a shaft, and back up every time.",
  },
  {
    id: "sealing", game: "terror", name: "The Sealing",
    achievements: [
      "terror:dungeon_clear", "terror:d1_all_secrets", "terror:oath_tablet",
      "terror:beat_jynxi", "terror:story_complete",
    ],
    titleText: "the One Who Closed the Seal",
    blurb: "The whole Unstable World walked, every secret found, and her smile finally slipped.",
  },
  {
    id: "solrender", game: "terror", name: "The Ladder",
    achievements: ["terror:first_pvp", "terror:pvp_10"],
    titleText: "the Ranked",
    blurb: "You played someone who could actually surprise you, and kept doing it.",
  },
  {
    id: "freehand", game: "terror", name: "Free Play",
    achievements: ["terror:first_freeplay", "terror:freeplay_50"],
    titleText: "the Unhurried",
    blurb: "Fifty games nobody was counting. You counted them.",
  },
  {
    id: "menagerie", game: "terror", name: "The Menagerie",
    achievements: [
      "terror:pet_100", "terror:radiant_first", "terror:pet_500", "terror:three_radiant",
    ],
    titleText: "the Well-Loved",
    blurb: "Five hundred small kindnesses, and three of them came back radiant.",
  },
  {
    id: "atelier", game: "terror", name: "The Wardrobe",
    achievements: ["terror:cosmetic_first", "terror:first_forge", "terror:all_starters"],
    titleText: "the Well-Dressed",
    blurb: "You dressed yourself, you sharpened your own deck, and you bought every shelf.",
  },
  {
    id: "affinities", game: "terror", name: "The Affinities",
    // ⭐ THERE ARE FOUR AFFINITIES AND THERE HAVE ONLY EVER BEEN FOUR:
    // X'aol, Y'tyranus, Enlightenment and Friend or Faux — four rows in
    // cards.js carrying an affinityType, and AFFINITY_TYPES has four
    // members. This title said FIVEFOLD and then insisted "there is no
    // sixth", which is two wrong numbers in eleven words, and it was
    // awarded by an achievement whose own trigger fired at THREE.
    // Somebody once counted the Affinities and got five, and every
    // string downstream inherited it.
    // Trigger raised to 4 in progression.js — see the note there.
    achievements: ["terror:five_affinity"],
    titleText: "the Fourfold",
    blurb: "Every Affinity, worn at least once. There is no fifth.",
  },

  // ── W'ILY'S PIER ────────────────────────────────────────────────────
  {
    id: "pier_walk", game: "pier", name: "The Pier",
    achievements: ["pier:first_exchange", "pier:wanderer"],
    titleText: "the Wanderer",
    blurb: "You changed money at the Well and then kept walking anyway.",
  },

  // ── SNAKE EYES ──────────────────────────────────────────────────────
  {
    id: "house", game: "casino", name: "The House",
    achievements: ["casino:first_roll", "casino:snake_eyes", "casino:high_roller"],
    titleText: "the High Roller",
    blurb: "You rolled, you rolled the worst thing there is, and you rolled anyway.",
  },

  // ── SOL SURVIVOR ────────────────────────────────────────────────────
  {
    id: "sol_party", game: "sol", name: "The Party",
    achievements: ["sol:first_dungeon", "sol:full_party"],
    titleText: "the Assembled",
    blurb: "Nobody goes down there alone twice.",
  },

  // ── PERRY ───────────────────────────────────────────────────────────
  {
    id: "perry_chain", game: "perry", name: "The Chain",
    achievements: ["perry:chain_10", "perry:chain_30", "perry:chain_50"],
    titleText: "the Unbroken Rhythm",
    blurb: "Fifty in a row without one bad beat.",
  },
  {
    id: "perry_road", game: "perry", name: "The Road",
    achievements: [
      "perry:first_parry", "perry:reach_hollowmere", "perry:reach_candlemarch",
      "perry:thrakk", "perry:typhiz", "perry:all_four",
    ],
    titleText: "the Long Walker",
    blurb: "Every mile of the Drowned Mile, and both things waiting at the end of it.",
  },
  {
    id: "perry_ascetic", game: "perry", name: "The Refusal",
    achievements: [
      "perry:never_upgraded", "perry:no_treasures", "perry:no_arts", "perry:untouched",
    ],
    titleText: "the One Who Took Nothing",
    blurb: "Offered everything, all the way down, and carried none of it.",
  },

  // ── ANNIHILATION ────────────────────────────────────────────────────
  {
    id: "the_fold", game: "annihilation", name: "The Fold",
    achievements: ["annihilation:first_fold", "annihilation:fold_ten"],
    titleText: "the Tenfold",
    blurb: "Ten folds deep and still standing where you started.",
  },

  // ── THE EMPIRE (cross-game) ─────────────────────────────────────────
  {
    id: "citizen", game: "empire", name: "Citizenship",
    achievements: [
      "empire:two_worlds", "empire:the_long_walk", "empire:coin_and_crest",
      "empire:fold_and_crown", "empire:citizen",
    ],
    titleText: "Citizen of the Illegal Empire",
    blurb: "Not a guest in any of them. A resident of all of them.",
  },
  {
    id: "unwritten", game: "empire", name: "The Unwritten",
    achievements: ["empire:the_unwritten"],
    titleText: "the Unwritten",
    blurb: "Nobody told you this was here.",
    hidden: true, // masked in the gallery until earned — same law as a hidden achievement
  },
];

export const TITLE_BY_ID = Object.fromEntries(TITLE_FAMILIES.map((f) => [f.id, f]));

// The cosmetic id a family's title occupies. ONE place decides this
// shape — a call site that builds the string by hand is a bug even
// when it happens to match today.
export const cosmeticIdFor = (familyId) => `title_${familyId}`;

// ── DERIVATION ────────────────────────────────────────────────────────

// The last achievement in the list is the capstone, always. Order in
// the table is therefore load-bearing, and says so here so nobody
// alphabetises it one day and quietly re-tiers a third of the titles.
export const capstoneOf = (familyId) => {
  const f = TITLE_BY_ID[familyId];
  if (!f || !f.achievements.length) return null;
  return f.achievements[f.achievements.length - 1];
};

// Authored tier wins; otherwise derive from the capstone's rank.
// Unknown falls to fable rather than null — a null tier means a title
// that renders as `undefined`, and a wrong-but-visible title beats an
// invisible one every time.
export const tierOf = (familyId) => {
  const f = TITLE_BY_ID[familyId];
  if (f && TITLE_TIER_META[f.tier]) return f.tier;
  const cap = capstoneOf(familyId);
  return RANK_TO_TIER[rankOf(cap)] || "fable";
};

export const tierMetaOf = (familyId) => TITLE_TIER_META[tierOf(familyId)];

// The tint a title wears — its ORIGIN GAME's colour, not its tier's.
// Deliberate: the tier is already spoken by the label beside it, and a
// Terror gold title reading as Terror across four games is the whole
// point of a cross-game wardrobe.
export const tintOf = (familyId) => {
  const f = TITLE_BY_ID[familyId];
  return GAME_NAMESPACES[f?.game]?.tint || GAME_NAMESPACES.empire.tint;
};

// The wearable string. Generated from the family name only if nobody
// authored one — and the generated shape is deliberately plain, so an
// unauthored title looks unfinished rather than looking canon.
export const textOf = (familyId) => {
  const f = TITLE_BY_ID[familyId];
  if (!f) return "";
  return f.titleText || `of ${f.name}`;
};

// ── PROGRESS ──────────────────────────────────────────────────────────
// achievementMap shape: { [id]: { earnedAt, ... } } — straight off
// profiles/{uid}/achievements. Missing map means zero, never a throw.

export const familyProgress = (achievementMap, familyId) => {
  const f = TITLE_BY_ID[familyId];
  if (!f) return { have: 0, need: 0, complete: false, missing: [] };
  const map = achievementMap || {};
  const missing = f.achievements.filter((id) => !map[id]);
  return {
    have: f.achievements.length - missing.length,
    need: f.achievements.length,
    complete: missing.length === 0,
    missing,
  };
};

// Every family id the player has completed.
export const earnedTitleIds = (achievementMap) =>
  TITLE_FAMILIES.filter((f) => familyProgress(achievementMap, f.id).complete).map((f) => f.id);

// The full gallery row set, sorted the way a gallery wants to read:
// earned first, then by tier descending, then by name. Pure — no
// Firebase, safe to call in a render.
export const titleGallery = (achievementMap) =>
  TITLE_FAMILIES.map((f) => {
    const prog = familyProgress(achievementMap, f.id);
    const tier = tierOf(f.id);
    return {
      id: f.id,
      game: f.game,
      name: f.hidden && !prog.complete ? "???" : f.name,
      text: f.hidden && !prog.complete ? "???" : textOf(f.id),
      blurb: f.hidden && !prog.complete ? "Nobody has told you this one is here." : f.blurb,
      masked: !!f.hidden && !prog.complete,
      tier,
      tierMeta: TITLE_TIER_META[tier],
      tint: tintOf(f.id),
      cosmeticId: cosmeticIdFor(f.id),
      ...prog,
    };
  }).sort((a, b) => {
    // Earned first — you came here to see what you have.
    if (a.complete !== b.complete) return a.complete ? -1 : 1;
    // MASKED-AND-UNEARNED SINK TO THE BOTTOM. Sorting by tier alone put
    // "???" at the very top of the list, because the Unwritten is a
    // mythic — which is the loudest possible placement for the one
    // entry the registry says is "community discovery only. Do not
    // surface in tutorials or marketing." Masking the NAME and then
    // ranking it first is surfacing it. Caught in the live lap.
    if (a.masked !== b.masked) return a.masked ? 1 : -1;
    const t = TITLE_TIER_META[b.tier].order - TITLE_TIER_META[a.tier].order;
    if (t) return t;
    return a.name.localeCompare(b.name);
  });

// ── THE WRITE ─────────────────────────────────────────────────────────
// Grant every title the player has completed and does not yet own.
//
// Idempotent by construction: grantCosmetic is append-only and returns
// { alreadyUnlocked: true } for one it already holds, so calling this
// after every achievement grant costs nothing and can never
// double-write. It does NOT equip — earning a title and choosing to
// wear it are two different acts, and silently changing what somebody's
// nameplate says is the rudest thing a progression system can do.
//
// Returns the ids newly minted this call (usually zero, occasionally
// one, which is exactly when a toast should fire).
export const syncTitles = async (uid, achievementMap) => {
  if (!uid) return [];
  const complete = earnedTitleIds(achievementMap);
  if (!complete.length) return [];

  // ONE read of what they already own, not one per family. The first
  // draft called grantCosmetic for every completed family on every app
  // boot, and grantCosmetic reads before it writes — so a player with
  // eight titles paid eight Firebase reads every launch to be told
  // eight times that nothing had changed. Found in the critique pass.
  // A failed read falls through to the per-family path rather than
  // skipping the sync: paying eight reads beats not minting a title.
  let owned = null;
  try { owned = await getUnlocked(uid); } catch (_) { owned = null; }

  const minted = [];
  for (const id of complete) {
    if (owned && owned[cosmeticIdFor(id)]) continue;
    const tier = tierOf(id);
    const res = await grantCosmetic(uid, cosmeticIdFor(id), {
      slot: "title",
      rarity: TIER_TO_COSMETIC_RARITY[tier],
      source: `titles:${id}`,
    });
    if (res?.success && !res.alreadyUnlocked) minted.push(id);
  }
  return minted;
};

// ── THE AUDIT ─────────────────────────────────────────────────────────
// A function, not a chore. Returns everything wrong with the table plus
// every achievement that belongs to no family — orphans are FINE (a
// one-shot is a real thing) but they should be a list somebody has
// looked at, not a silence.
export const auditFamilies = () => {
  const claimed = new Set();
  const unknownMembers = [];
  const emptyFamilies = [];
  const duplicateIds = [];
  const seen = new Set();

  for (const f of TITLE_FAMILIES) {
    if (seen.has(f.id)) duplicateIds.push(f.id);
    seen.add(f.id);
    if (!f.achievements || !f.achievements.length) emptyFamilies.push(f.id);
    for (const a of f.achievements || []) {
      claimed.add(a);
      if (!ACHIEVEMENTS[a]) unknownMembers.push({ family: f.id, achievement: a });
    }
  }

  const orphans = Object.keys(ACHIEVEMENTS).filter((id) => !claimed.has(id));
  return {
    families: TITLE_FAMILIES.length,
    duplicateIds,
    emptyFamilies,
    unknownMembers,
    orphans,
    ok: !duplicateIds.length && !emptyFamilies.length && !unknownMembers.length,
  };
};

// titles.js owns which families exist; CosmeticManifest owns what they
// read as. Two tables that must agree are two tables that will drift, so
// the drift is a function rather than a hope. Console one-liner in any
// session: `auditTitleStrings()`.
// `title_war_*` is H5's week spoils, not a family — a family is a
// lifetime arc, a war title is seven days of showing up. Same slot, same
// equip, different earn path, so it is skipped here rather than reported
// forever as an orphan nobody is going to adopt.
const NOT_A_FAMILY = /^title_war_/;

export const auditTitleStrings = () => {
  const manifest = Object.keys(COSMETICS.title || {})
    .filter((k) => k.startsWith("title_") && !NOT_A_FAMILY.test(k));
  const wanted = TITLE_FAMILIES.map((f) => cosmeticIdFor(f.id));
  const has = new Set(manifest);
  const want = new Set(wanted);
  const missingString = wanted.filter((k) => !has.has(k));
  const orphanString = manifest.filter((k) => !want.has(k));
  return { missingString, orphanString, ok: !missingString.length && !orphanString.length };
};

const titles = {
  TITLE_TIERS, TITLE_TIER_META, TITLE_FAMILIES, TITLE_BY_ID,
  cosmeticIdFor, capstoneOf, tierOf, tierMetaOf, tintOf, textOf,
  familyProgress, earnedTitleIds, titleGallery, syncTitles,
  auditFamilies, auditTitleStrings,
};
export default titles;
